import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import pluralize from 'pluralize';
import moment from 'moment';
import ms from 'ms';

import makeFullName from 'common/util/makeFullName';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import { SR_API_ACTION_RUN_CREDIT_AND_BACKGROUND_REPORT } from 'api/actionTypes';

import { isRenterQualifiedToRunCreditAndBackgroundReport } from 'common/householdApplication/prequalificationHelpers';

import log from 'client/lib/log';
import postWithState from 'client/actions/postWithState';

import SrText from 'client/components/SrText';
import SrSimpleMessageModal from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import './SrHouseholdTenantAlertStatusBox.less';

const debug = log.debug('sunroom:components:SrHouseholdTenantAlertStatusBox');

const getMissingTenantAlertReports = ({
  roommates,
  guarantors,
  roommateApplications,
  guarantorApplications,
  tenantAlertReports,
}) => {
  const tenantAlertReportByUserId = tenantAlertReports.reduce(
    (memo, tenantAlerReport) => ({
      ...memo,
      [tenantAlerReport.userId]: tenantAlerReport,
    }),
    {},
  );

  const usersIds = [
    ...roommates.map((renter) => renter.id),
    ...guarantors.map((guarantor) => guarantor.id),
  ];

  const householdApplicationsByUserId = [...roommateApplications, ...guarantorApplications].reduce(
    (memo, householdApplication) => ({
      ...memo,
      [householdApplication.userId]: householdApplication,
    }),
    {},
  );

  const userIdsWithTenantAlertReport = usersIds.filter((userId) => {
    return tenantAlertReportByUserId[userId];
  });

  const missingTenantAlerReportsByUserId = usersIds.filter((userId) => {
    return !tenantAlertReportByUserId[userId];
  });

  const missingTenantAlertReports = Object.keys(missingTenantAlerReportsByUserId).length > 0;

  const userIdsQualifiedToRunCreditAndBackgroundCheck = missingTenantAlerReportsByUserId.filter(
    (userId) => {
      const householdApplication = householdApplicationsByUserId[userId];
      if (!householdApplication) {
        return false;
      }
      const { isQualified } = isRenterQualifiedToRunCreditAndBackgroundReport({
        householdApplication,
      });
      return isQualified;
    },
  );

  const userIdsNotQualifiedToRunCreditAndBackgroundCheck = missingTenantAlerReportsByUserId.filter(
    (userId) => {
      return !userIdsQualifiedToRunCreditAndBackgroundCheck.includes(userId);
    },
  );

  return {
    usersIds,
    userIdsWithTenantAlertReport,
    missingTenantAlertReports,
    missingTenantAlerReportsByUserId,
    userIdsQualifiedToRunCreditAndBackgroundCheck,
    userIdsNotQualifiedToRunCreditAndBackgroundCheck,
    householdApplicationsByUserId,
  };
};

const getTenantAlerReportMessage = ({ tenantAlertReports }) => {
  const now = moment();

  const userIdsByTenantAlertReportCreatedInDays = tenantAlertReports.reduce(
    (memo, tenantAlerReport) => {
      const days = now.diff(tenantAlerReport.createdDate, 'days');
      memo[days] = memo[days] || [];
      memo[days].push(tenantAlerReport.userId);
      return memo;
    },
    {},
  );

  return (
    Object.keys(userIdsByTenantAlertReportCreatedInDays)
      .map((days) => {
        return `${pluralize(
          'Applicant',
          userIdsByTenantAlertReportCreatedInDays[days].length,
          true,
        )} ran ${days === '0' ? 'Today' : `${pluralize('day', days, true)} ago`}.`;
      })
      .join(' ') + ' '
  );
};

const getConfirmationMessage = ({
  usersIds,
  userIdsQualifiedToRunCreditAndBackgroundCheck,
  userIdsNotQualifiedToRunCreditAndBackgroundCheck,
  householdApplicationsByUserId,
}) => {
  if (userIdsQualifiedToRunCreditAndBackgroundCheck.length === usersIds.length) {
    const userCount =
      userIdsQualifiedToRunCreditAndBackgroundCheck.length > 1
        ? ` on all ${pluralize(
            'applicant',
            userIdsQualifiedToRunCreditAndBackgroundCheck.length,
            true,
          )}`
        : ' for 1 appliant';

    return `Are you sure you want to run a credit & background check ${userCount} in this household?`;
  }

  const userReportDOM = userIdsNotQualifiedToRunCreditAndBackgroundCheck.map((userId) => {
    const householdApplication = householdApplicationsByUserId[userId];
    const userName = makeFullName(householdApplication.firstName, householdApplication.lastName);
    return (
      <span key={userId}>
        {`${userName} - (${householdApplication.phone || 'NA'})`}
        <br />
      </span>
    );
  });

  return (
    <span>
      {userIdsNotQualifiedToRunCreditAndBackgroundCheck.length > 0 && (
        <>
          {`${pluralize(
            'applicant',
            userIdsNotQualifiedToRunCreditAndBackgroundCheck.length,
            true,
          )} doesn't have enough info to run a check: `}
          <br />
          <br />
          {userReportDOM}
          <br />
          <br />
        </>
      )}
      {'Are you sure you want to run a credit & background check on the other ' +
        `${pluralize('applicant', userIdsQualifiedToRunCreditAndBackgroundCheck.length, true)}` +
        ' in this household?'}
    </span>
  );
};

const SrHouseholdTenantAlertStatusBox = ({
  listingId,
  roommates,
  guarantors,
  roommateApplications,
  guarantorApplications,
  tenantAlertReports,
  reloadHouseholdCard,
  onRunningTenantAlert,
  dispatch,
}) => {
  const [isConfimationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [inProgress, setInProgress] = useState(false);

  const {
    usersIds,
    userIdsWithTenantAlertReport,
    missingTenantAlertReports,
    missingTenantAlerReportsByUserId,
    userIdsQualifiedToRunCreditAndBackgroundCheck,
    userIdsNotQualifiedToRunCreditAndBackgroundCheck,
    householdApplicationsByUserId,
  } = getMissingTenantAlertReports({
    roommates,
    guarantors,
    roommateApplications,
    guarantorApplications,
    tenantAlertReports,
  });

  debug({
    usersIds,
    userIdsWithTenantAlertReport,
    missingTenantAlertReports,
    missingTenantAlerReportsByUserId,
    userIdsQualifiedToRunCreditAndBackgroundCheck,
    userIdsNotQualifiedToRunCreditAndBackgroundCheck,
    householdApplicationsByUserId,
  });

  const contentDOM = [];
  const boxDisabled =
    !missingTenantAlertReports ||
    !userIdsQualifiedToRunCreditAndBackgroundCheck.length ||
    inProgress;

  const toggleConfirmationModal = () => {
    setIsConfirmationModalOpen(!isConfimationModalOpen);
  };

  const runCreditAndBackgroundReports = () => {
    toggleConfirmationModal();
    setInProgress(true);
    onRunningTenantAlert(
      userIdsQualifiedToRunCreditAndBackgroundCheck.reduce(
        (memo, userId) => ({
          ...memo,
          [userId]: true,
        }),
        {},
      ),
    );

    Promise.all(
      userIdsQualifiedToRunCreditAndBackgroundCheck.map((userId) => {
        const householdApplication = [...roommateApplications, ...guarantorApplications].find(
          (householdApplicationd) => userId === householdApplicationd.userId,
        );
        return dispatch(postWithState, {
          action: SR_API_ACTION_RUN_CREDIT_AND_BACKGROUND_REPORT,
          queryId: String(userId),
          timeout: ms('2m'),
          payload: {
            userId,
            listingId,
            householdApplicationId: householdApplication.id,
          },
        });
      }),
    )
      .then(reloadHouseholdCard)
      .then(() => {
        setInProgress(false);
        onRunningTenantAlert({});
      });
  };

  if (!missingTenantAlertReports) {
    const latestReportDate = Math.max(
      ...tenantAlertReports.map((report) => new Date(report.createdDate).getTime()),
    );
    contentDOM.push(
      <SrText color="white" weight="700" key="left">
        Credit has been ran for all applicants!
      </SrText>,
    );
    contentDOM.push(
      <SrText color="white" weight="bold" fontStyle="italic" key="right">
        Ran&nbsp;
        {momentWithAustinTimezone(latestReportDate).format('MMM D [at] h:mma')}
      </SrText>,
    );
  } else {
    contentDOM.push(
      <SrText color="white" weight="700" key="left">
        {userIdsQualifiedToRunCreditAndBackgroundCheck.length
          ? `Run credit for ${userIdsQualifiedToRunCreditAndBackgroundCheck.length}/${usersIds.length} applicants` // eslint-disable-line max-len
          : 'Not enough info.'}
      </SrText>,
    );

    if (
      usersIds.length === missingTenantAlerReportsByUserId.length &&
      (!userIdsNotQualifiedToRunCreditAndBackgroundCheck.length ||
        usersIds.length === userIdsNotQualifiedToRunCreditAndBackgroundCheck.length)
    ) {
      contentDOM.push(
        <SrText color="white" weight="700" key="right">
          {usersIds.length === missingTenantAlerReportsByUserId.length &&
            'No previous checks found'}
        </SrText>,
      );
    } else if (
      userIdsNotQualifiedToRunCreditAndBackgroundCheck.length ||
      userIdsWithTenantAlertReport.length
    ) {
      const notRunningForUsersCount =
        userIdsNotQualifiedToRunCreditAndBackgroundCheck.length +
        userIdsWithTenantAlertReport.length;

      contentDOM.push(
        <div key="right">
          <SrText color="white" weight="500" fontStyle="italic">
            Non running for {pluralize('applicant', notRunningForUsersCount, true)}:
          </SrText>{' '}
          {userIdsWithTenantAlertReport.length > 0 && (
            <SrText color="white" weight="700" fontStyle="italic">
              {getTenantAlerReportMessage({
                userIdsWithTenantAlertReport,
                tenantAlertReports,
              })}
            </SrText>
          )}
          {userIdsNotQualifiedToRunCreditAndBackgroundCheck.length > 0 && (
            <SrText color="white" weight="700" fontStyle="italic">
              {pluralize(
                'Applicant',
                userIdsNotQualifiedToRunCreditAndBackgroundCheck.length,
                true,
              )}
              {' not enough info.'}
            </SrText>
          )}
        </div>,
      );
    }
  }

  return (
    <div
      styleName={classnames('component', {
        ok: !missingTenantAlertReports,
        disabled: boxDisabled,
      })}
      onClick={boxDisabled ? null : toggleConfirmationModal}
    >
      {contentDOM}

      {isConfimationModalOpen && (
        <SrSimpleMessageModal
          headline="Run Credit Report"
          message={getConfirmationMessage({
            usersIds,
            userIdsQualifiedToRunCreditAndBackgroundCheck,
            userIdsNotQualifiedToRunCreditAndBackgroundCheck,
            householdApplicationsByUserId,
          })}
          buttonText="yes, run now"
          subLabelText="No"
          onButtonClick={runCreditAndBackgroundReports}
          onSubLabelClick={toggleConfirmationModal}
          maxWidth={375}
          onClose={toggleConfirmationModal}
        />
      )}
    </div>
  );
};

SrHouseholdTenantAlertStatusBox.propTypes = {
  listingId: PropTypes.number.isRequired,
  roommates: PropTypes.array.isRequired,
  guarantors: PropTypes.array.isRequired,
  roommateApplications: PropTypes.array.isRequired,
  guarantorApplications: PropTypes.array.isRequired,
  tenantAlertReports: PropTypes.array.isRequired,
  reloadHouseholdCard: PropTypes.func.isRequired,
  onRunningTenantAlert: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
};

SrHouseholdTenantAlertStatusBox.displayName = 'SrHouseholdTenantAlertStatusBox';

export default SrHouseholdTenantAlertStatusBox;
